<script setup lang="ts">
import SvgIcon from "~/components/Common/SvgIcon.vue";

interface Props {
  type?: "solid" | "outlined" | "link";
  label: string;
  icon?: string;
  disabled?: boolean;
  iconSize?: string;
  loading?: boolean;
  reverseIcon?: boolean;
  variant?: "default" | "error";
  nativeType?: "button" | "submit" | "reset";
}

const props = withDefaults(defineProps<Props>(), {
  type: "solid",
  disabled: false,
  iconSize: "w-[16px] h-[16px]",
  loading: false,
  reverseIcon: false,
  variant: "default",
  nativeType: "button",
});

// The native event is passed on so that a caller's `@click.stop` / `@click.prevent` works.
const emit = defineEmits<{
  click: [event: MouseEvent];
}>();

const variantClass = computed(() => {
  const { type, variant } = props;

  if (type === "solid") {
    return variant === "error" ? "button_danger" : "button__default";
  }
  if (type === "outlined") {
    return variant === "error" ? "button_border__error" : "button_border__default";
  }
  // link
  return variant === "error" ? "button_secondary_cancel_default" : "button_secondary_default";
});
</script>

<template>
  <button
    :type="nativeType"
    class="app-button justify-center flex flex-row items-center gap-1"
    :class="[
      variantClass,
      { 'flex-row-reverse': reverseIcon, 'border border-transparent': type === 'solid', 'h-8': type !== 'link' },
    ]"
    :disabled="disabled || loading"
    @click="(event: MouseEvent) => emit('click', event)"
  >
    <SvgIcon v-if="icon" :name="icon" :class="iconSize" />
    <span :class="type === 'link' ? 'body_regular' : 'body_med'">{{ label }}</span>
  </button>
</template>

<style scoped>
/* solid/outlined have their own :disabled styles in global SCSS — only link needs it */
.app-button.button_secondary_default:disabled,
.app-button.button_secondary_cancel_default:disabled {
  @apply cursor-default;
  color: var(--grey-medium-light);
}
</style>
